import { GaugeProps, gaugeClasses, useGaugeState } from '@mui/x-charts';
import { Box } from '@mui/material';
import React, { useState } from 'react';

import ButtonGroup from 'components/button-group';
import GaugeChart from 'components/charts/gauge-chart';
import { useChartAnimation } from 'components/charts/hooks/use-chart-animation';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';
import { useMetricFormatters } from 'views/playground/features/data-lab/hooks/use-metric-formatters';
import { HEALTH_STATUS_COLOR, healthStatusFor, UTILIZATION_THRESHOLDS } from 'views/playground/features/data-lab/utils/health-status';
import { HealthThresholds } from 'views/playground/features/data-lab/types/health-status.type';

type Props = Pick<GaugeProps, 'height'> & {
  data: { value: number; max: number };
};

type ColorMode = 'gradient' | 'solid';

const UTILIZATION_TICKS = Object.values(UTILIZATION_THRESHOLDS);
const GRADIENT_SEGMENT_COUNT = 100;

const getGradientColor = (value: number, palette: ChartPalette, thresholds: HealthThresholds): string => {
  const mix = (start: string, end: string, amount: number) =>
    `color-mix(in srgb, ${start} ${(1 - amount) * 100}%, ${end} ${amount * 100}%)`;

  if (value <= thresholds.watch) {
    return mix(palette.status.green, palette.status.amber, Math.max(0, value / thresholds.watch));
  }
  if (value <= thresholds.critical) {
    return mix(palette.status.amber, palette.status.red, (value - thresholds.watch) / (thresholds.critical - thresholds.watch));
  }
  return palette.status.red;
};

const pointOnCircle = (cx: number, cy: number, radius: number, angle: number) => ({
  x: cx + radius * Math.sin(angle),
  y: cy - radius * Math.cos(angle),
});

// MUI's SVG arc has a single fill, so render adjacent slices to carry the gradient along the gauge's circumference.
const GaugeGradientArc: React.FC<{ palette: ChartPalette; thresholds: HealthThresholds }> = ({ palette, thresholds }) => {
  const { value, valueMin, valueMax, startAngle, endAngle, innerRadius, outerRadius, cx, cy } = useGaugeState();
  if (value === null || valueMax <= valueMin) return null;

  const progress = Math.max(0, Math.min(1, (value - valueMin) / (valueMax - valueMin)));
  const segmentCount = Math.ceil(progress * GRADIENT_SEGMENT_COUNT);
  const sweep = endAngle - startAngle;

  return (
    <>
      {Array.from({ length: segmentCount }, (_, index) => {
        const startRatio = index / GRADIENT_SEGMENT_COUNT;
        const endRatio = Math.min((index + 1) / GRADIENT_SEGMENT_COUNT, progress);
        const segmentStartAngle = startAngle + sweep * startRatio;
        const segmentEndAngle = startAngle + sweep * endRatio;
        const valueAtSegmentCenter = valueMin + ((startRatio + endRatio) / 2) * (valueMax - valueMin);
        const outerStart = pointOnCircle(cx, cy, outerRadius, segmentStartAngle);
        const outerEnd = pointOnCircle(cx, cy, outerRadius, segmentEndAngle);
        const innerEnd = pointOnCircle(cx, cy, innerRadius, segmentEndAngle);
        const innerStart = pointOnCircle(cx, cy, innerRadius, segmentStartAngle);
        const color = getGradientColor(valueAtSegmentCenter, palette, thresholds);
        const path = [
          `M ${outerStart.x} ${outerStart.y}`,
          `A ${outerRadius} ${outerRadius} 0 0 1 ${outerEnd.x} ${outerEnd.y}`,
          `L ${innerEnd.x} ${innerEnd.y}`,
          `A ${innerRadius} ${innerRadius} 0 0 0 ${innerStart.x} ${innerStart.y}`,
          'Z',
        ].join(' ');

        return <path key={index} d={path} fill={color} />;
      })}
    </>
  );
};

// Radial ticks show status boundaries without dividing the reference arc into
// bands. This component must remain inside Gauge because it reads
// `useGaugeState()`.
const GaugeThresholdTicks: React.FC<{ thresholds: readonly number[]; color: string }> = ({ thresholds, color }) => {
  const { startAngle, endAngle, valueMin, valueMax, innerRadius, outerRadius, cx, cy } = useGaugeState();

  return (
    <>
      {thresholds
        .filter((threshold) => threshold > valueMin && threshold < valueMax)
        .map((threshold) => {
          // Same angle formula `GaugeProvider` uses for `valueAngle` (radians,
          // 0 at 12 o'clock, increasing clockwise, per the d3-shape `arc()`
          // convention `GaugeReferenceArc`/`GaugeValueArc` are built on).
          const angle = startAngle + (endAngle - startAngle) * ((threshold - valueMin) / (valueMax - valueMin));
          const x1 = cx + innerRadius * Math.sin(angle);
          const y1 = cy - innerRadius * Math.cos(angle);
          const x2 = cx + outerRadius * Math.sin(angle);
          const y2 = cy - outerRadius * Math.cos(angle);
          return <line key={threshold} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={3} />;
        })}
    </>
  );
};

// Not selectable: `Gauge` has no per-item click API at all.
const UtilizationGauge: React.FC<Props> = ({ data, height = 320 }) => {
  const { formatPercent } = useMetricFormatters();
  const palette = useChartPalette();
  const chartSkipAnimation = useChartAnimation();
  const [colorMode, setColorMode] = useState<ColorMode>('gradient');
  const solidColor = palette.status[HEALTH_STATUS_COLOR[healthStatusFor(data.value, UTILIZATION_THRESHOLDS)]];

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="flex-end">
        <ButtonGroup
          aria-label="Color mode"
          value="utilization-color-mode"
          buttons={[
            { label: 'Gradient', value: 'left', state: false },
            { label: 'Solid', value: 'right', state: true },
          ]}
          handleClick={(isSolid) => setColorMode(isSolid ? 'solid' : 'gradient')}
        />
      </Box>
      <GaugeChart
        height={height}
        skipAnimation={chartSkipAnimation}
        value={data.value}
        valueMin={0}
        valueMax={data.max}
        text={({ value }) => (value === null ? '' : formatPercent(value))}
        sx={{
          [`& .${gaugeClasses.valueArc}`]: { fill: colorMode === 'gradient' ? 'transparent' : solidColor },
          [`& .${gaugeClasses.referenceArc}`]: { fill: palette.backgrounds.sunken },
          [`& .${gaugeClasses.valueText} text`]: { fill: palette.text.primary, fontSize: 28, fontWeight: 700 },
        }}
      >
        {colorMode === 'gradient' && <GaugeGradientArc palette={palette} thresholds={UTILIZATION_THRESHOLDS} />}
        <GaugeThresholdTicks thresholds={UTILIZATION_TICKS} color={palette.text.secondary} />
      </GaugeChart>
    </Box>
  );
};

export default UtilizationGauge;
